import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  Check,
  Copy,
  Globe,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  Timer,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import { SITE_URL } from "@/lib/site-url";

export const Route = createFileRoute("/_app/email")({
  component: EmailPage,
  head: () => ({
    meta: [
      { title: "Email Activations — SmsEmpire" },
      {
        name: "description",
        content:
          "Generate disposable emails on Gmail, Outlook, iCloud, Yandex and more to verify any website in seconds. Instant delivery, no personal data required.",
      },
      { property: "og:title", content: "Email Activations — SmsEmpire" },
      {
        property: "og:description",
        content:
          "Disposable emails on real domains — delivered instantly, no sign-up data exposed.",
      },
      { property: "og:url", content: `${SITE_URL}/email` },
      { property: "og:image", content: `${SITE_URL}/og/default.png` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/email` }],
  }),
});

type EmailDomain = {
  name: string;
  cost: number;
  count: number;
};

/** The site being verified — real service artwork when we have it, initial otherwise. */
function SiteIcon({ site }: { site: string }) {
  const url = siteIconUrl(site);
  if (!url) return <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
  return (
    <img
      src={url}
      alt=""
      aria-hidden="true"
      loading="lazy"
      className="h-4 w-4 shrink-0 rounded"
      onError={(e) => {
        (e.currentTarget as HTMLImageElement).style.display = "none";
      }}
    />
  );
}

/**
 * Quick picks. The provider only delivers mail *sent from* the site's domain (measured:
 * same sender → delivered in 15 s, other sender → never), so these are the domains the
 * services send verification mail from. Telegram is telegram.org — telegram.com is an
 * unrelated company, and it used to be this page's example.
 */
const POPULAR_SITES: { label: string; domain: string }[] = [
  { label: "Telegram", domain: "telegram.org" },
  { label: "WhatsApp", domain: "whatsapp.com" },
  { label: "Instagram", domain: "instagram.com" },
  { label: "Facebook", domain: "facebook.com" },
  { label: "TikTok", domain: "tiktok.com" },
  { label: "X / Twitter", domain: "x.com" },
  { label: "Discord", domain: "discord.com" },
  { label: "ChatGPT", domain: "openai.com" },
  { label: "Google", domain: "google.com" },
  { label: "Amazon", domain: "amazon.com" },
  { label: "Apple", domain: "apple.com" },
  { label: "Microsoft", domain: "microsoft.com" },
];

type EmailActivation = {
  id: number;
  email: string;
  status: string;
  cost: number;
  hero_id: number;
  activation_id: number;
  code?: string;
};

import { DeliveredCode } from "@/components/delivered-code";
import { useI18n } from "@/lib/i18n";
import { useActivations } from "@/lib/queries";
import { copyToClipboard } from "@/lib/utils";
import { cancelSecondsLeft } from "@/lib/cancel-lock";
import { formatServerDateTime } from "@/lib/server-time";
import { siteIconUrl } from "@/lib/site-icons";
import { DomainIcon } from "@/components/domain-icon";

function EmailPage() {
  const { t } = useI18n();
  const { format } = useCurrency();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [target, setTarget] = useState("");
  const [domainSearch, setDomainSearch] = useState("");
  const [domains, setDomains] = useState<EmailDomain[]>([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  // The site as the backend normalised it ("Telegram" → "telegram.org"): what the inbox
  // will actually accept mail from. Null while nothing usable has been typed.
  const [resolvedSite, setResolvedSite] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<EmailDomain | null>(null);

  const filtered = useMemo(
    () => domains.filter((d) => d.name.toLowerCase().includes(domainSearch.trim().toLowerCase())),
    [domainSearch, domains],
  );

  async function loadDomains(site?: string) {
    setLoadingDomains(true);
    try {
      const url = site
        ? `/api/email/domains?site=${encodeURIComponent(site)}`
        : "/api/email/domains";
      const data = await api.get<{ site?: string | null; domains?: EmailDomain[] }>(url);
      setDomains(data.domains || []);
      // Only a site the user typed counts; the default listing is just for prices.
      setResolvedSite(site ? (data.site ?? null) : null);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("email.domains_failed"));
    } finally {
      setLoadingDomains(false);
    }
  }

  useEffect(() => {
    loadDomains();
  }, []);

  // Reload domains when target changes (to filter by site)
  useEffect(() => {
    if (target.trim()) {
      const debounce = setTimeout(() => loadDomains(target.trim()), 600);
      return () => clearTimeout(debounce);
    }
    setResolvedSite(null);
  }, [target]);

  // Reuses the shared activation cache instead of running a third 5s poll against
  // the same endpoint; the email view is just a filtered projection of it.
  const { data: allActivations = [], refetch: fetchEmailActivations } = useActivations();
  const activations = useMemo(
    () =>
      allActivations
        .filter(
          (a) =>
            a.activation_type === "email" && (a.status === "pending" || a.status === "received"),
        )
        .map((a) => ({
          id: a.id,
          email: a.number || "",
          status: a.status,
          cost: a.cost,
          hero_id: a.hero_id,
          activation_id: a.id,
          code: a.code,
          created_at: a.created_at,
          target_site: a.target_site,
          domain: a.country,
        })),
    [allActivations],
  );

  // Ticks only while something is still inside its cancel window, so the page is not
  // re-rendering every second for nothing.
  const [now, setNow] = useState(() => Date.now());
  const anyLocked = activations.some(
    (a) => a.status === "pending" && cancelSecondsLeft(a.created_at, now) > 0,
  );
  useEffect(() => {
    if (!anyLocked) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [anyLocked]);

  async function purchase(domain: string) {
    if (!resolvedSite) {
      toast.error(t("email.pick_site_first"));
      return;
    }
    setPurchasing(true);
    try {
      const data = await api.post<{ email: string }>("/api/email/request", {
        domain,
        site: resolvedSite,
      });
      toast.success(`Email: ${data.email}`);
      await Promise.all([refreshUser(), fetchEmailActivations()]);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("common.error"));
    } finally {
      setPurchasing(false);
    }
  }

  async function cancelEmail(activationId: number) {
    try {
      const data = await api.post<{ refunded?: number }>(`/api/activations/${activationId}/cancel`);
      toast.success(t("activations.cancelled_refund", { amount: format(data?.refunded ?? 0) }));
      await Promise.all([refreshUser(), fetchEmailActivations()]);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("common.error"));
    }
  }

  async function copy(text: string) {
    if (!text) return;
    const ok = await copyToClipboard(text);
    if (ok) {
      toast.success(t("sms.copied"));
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("email.title")}</h1>
        <p className="mt-1 text-muted-foreground">{t("email.subtitle")}</p>
      </div>

      <div className="flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4 text-sm">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
        <p className="font-medium text-foreground">{t("email.warning")}</p>
      </div>

      {/* Active email activations */}
      {activations.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-base font-semibold">{t("email.active_emails")}</h2>
          {activations.map((a) => (
            <div
              key={a.id}
              className={`rounded-xl border p-4 ${a.status === "received" ? "border-emerald-500/30 bg-emerald-500/5" : "border-border bg-card"}`}
            >
              <div className="flex items-center gap-3">
                <DomainIcon domain={a.domain || a.email.split("@")[1] || ""} />
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                    {a.target_site && (
                      <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                        <SiteIcon site={a.target_site} />
                        {a.target_site}
                      </span>
                    )}
                    <span>·</span>
                    <span>{format(a.cost)}</span>
                    <span>·</span>
                    <span>{formatServerDateTime(a.created_at)}</span>
                    <span
                      className={`rounded-full px-2 py-0.5 font-medium ${
                        a.status === "received"
                          ? "bg-emerald-500/15 text-emerald-400"
                          : "bg-amber-500/15 text-amber-400"
                      }`}
                    >
                      {a.status === "received"
                        ? t("activations.status_received")
                        : t("activations.status_pending")}
                    </span>
                  </div>
                  <button
                    onClick={() => copy(a.email)}
                    className="inline-flex items-center gap-2 font-mono text-sm hover:text-primary transition-colors break-all text-left"
                  >
                    {a.email}
                    <Copy className="h-3.5 w-3.5 shrink-0" />
                  </button>
                  {a.code ? (
                    <DeliveredCode code={a.code} onCopy={copy} />
                  ) : (
                    <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {t("email.waiting_code")}
                    </div>
                  )}
                </div>
                {(() => {
                  const locked = a.status === "pending" ? cancelSecondsLeft(a.created_at, now) : 0;
                  return (
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={locked > 0}
                        onClick={() => cancelEmail(a.activation_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <X className="h-4 w-4 mr-1 shrink-0" />
                        {locked > 0 ? t("activations.cancel_locked") : t("email.cancel")}
                      </Button>
                      {locked > 0 && (
                        <span className="flex items-center gap-1 text-[11px] text-amber-400 font-mono font-medium whitespace-nowrap">
                          <Timer className="h-3 w-3 animate-spin shrink-0" />
                          {Math.floor(locked / 60)}:{(locked % 60).toString().padStart(2, "0")} min
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
          <p className="text-xs text-muted-foreground text-right">
            <button
              onClick={() => navigate({ to: "/activations" })}
              className="underline hover:text-foreground"
            >
              {t("email.view_all")}
            </button>
          </p>
        </div>
      )}

      {/* Site input */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-elegant">
        <div className="space-y-2">
          <Label htmlFor="target">{t("email.site_to_verify")}</Label>
          <Input
            id="target"
            placeholder={t("email.site_ph")}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          />
          <div className="flex flex-wrap gap-1.5 pt-1">
            {POPULAR_SITES.map((p) => (
              <button
                key={p.domain}
                type="button"
                onClick={() => setTarget(p.domain)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors",
                  resolvedSite === p.domain
                    ? "border-primary/60 bg-primary/10 text-foreground"
                    : "border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground",
                )}
              >
                <SiteIcon site={p.domain} />
                {p.label}
              </button>
            ))}
          </div>
          {target.trim() && !loadingDomains && (
            <p
              className={cn(
                "text-xs font-medium",
                resolvedSite ? "text-emerald-500" : "text-destructive",
              )}
            >
              {resolvedSite
                ? t("email.site_resolved", { site: resolvedSite })
                : t("email.site_invalid")}
            </p>
          )}
          {/* The one rule that decides whether the code ever arrives. */}
          <p className="text-xs text-muted-foreground">{t("email.sender_rule")}</p>
        </div>
      </div>

      {/* Domain picker */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-elegant">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold">{t("email.choose_domain")}</h2>
          <div className="flex items-center gap-2">
            <div className="relative sm:w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={domainSearch}
                onChange={(e) => setDomainSearch(e.target.value)}
                placeholder={t("email.search_domain")}
                className="pl-9"
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => loadDomains(target || undefined)}
              disabled={loadingDomains}
            >
              <RefreshCw className={`h-4 w-4 ${loadingDomains ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {!resolvedSite && !loadingDomains && filtered.length > 0 && (
          <p className="mb-3 text-xs text-muted-foreground">{t("email.pick_site_first")}</p>
        )}
        {loadingDomains ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            {t("email.no_domains_available")}
          </p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {filtered.map((d) => (
              <li key={d.name}>
                <button
                  type="button"
                  onClick={() => setConfirming(d)}
                  disabled={purchasing || !resolvedSite}
                  title={!resolvedSite ? t("email.pick_site_first") : undefined}
                  className={cn(
                    "flex w-full items-center justify-between gap-3 rounded-xl border border-border bg-background p-3 text-left transition hover:border-primary/50 hover:shadow-glow disabled:opacity-50 disabled:cursor-not-allowed",
                  )}
                >
                  <span className="flex min-w-0 items-center gap-2.5 text-sm font-medium">
                    <DomainIcon domain={d.name} className="h-8 w-8 text-xs" />
                    <span className="min-w-0">
                      <span className="block truncate">@{d.name}</span>
                      {d.count > 0 && (
                        <span className="text-xs font-normal text-muted-foreground">
                          {d.count} {t("email.available_short")}
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="text-sm font-semibold">{format(d.cost)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Every click on a domain used to be an immediate, paid purchase. */}
      <Dialog open={confirming !== null} onOpenChange={(o) => !o && setConfirming(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("email.confirm_title")}</DialogTitle>
            <DialogDescription>
              {confirming &&
                resolvedSite &&
                t("email.confirm_desc", {
                  domain: `@${confirming.name}`,
                  site: resolvedSite,
                  price: format(confirming.cost),
                })}
            </DialogDescription>
          </DialogHeader>
          <p className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-xs text-foreground">
            {resolvedSite && t("email.confirm_sender_warning", { site: resolvedSite })}
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirming(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={purchasing}
              onClick={async () => {
                const d = confirming;
                setConfirming(null);
                if (d) await purchase(d.name);
              }}
            >
              {purchasing && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {t("email.confirm_buy", { price: confirming ? format(confirming.cost) : "" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
